import { Animation, mustBeHTMLElement } from "silver";

type ArcToOptions = {
    /** Whether to clone the ref element, or move the element itself */
    clone: boolean;
    /** A class to apply to the clone */
    cloneClass: string;
    /** The curve of the arc. Either a constant number, or a function based on the distance */
    arcHeight: number|((distance: number) => number);
    /** Animation duration (milliseconds). Either a constant number, or a function based on the distance */
    duration: number|((distance: number) => number);
    /** When true, the target position will be forced to near the viewport */
    clipToViewport: boolean;
    /** Animates opacity */
    opacity: (time: number) => number;
    /** Animate other transform properties */
    extraTransforms: (time: number) => string;
};

export function arcElementTo(ref: HTMLElement, to: HTMLElement, options: Partial<ArcToOptions> = { }): Promise<void> {

	const { top: fromTop, left: fromLeft, width: refWidth } = ref.getBoundingClientRect();
	let { top: toTop, left: toLeft } = to.getBoundingClientRect();

	if (options.clipToViewport) {
		toTop = Math.max(-200, Math.min(toTop, window.innerHeight + 200));
		toLeft = Math.max(-200, Math.min(toLeft, window.innerWidth + 200));
	}

	const dx = toLeft - fromLeft;
	const dy = toTop - fromTop;

	if (options.clone) {
		ref = mustBeHTMLElement(ref.cloneNode(true));
		if (options.cloneClass) {
			ref.classList.add(options.cloneClass);
		}
		ref.style.left = `${fromLeft}px`;
		ref.style.top = `${fromTop}px`;
		ref.style.width = `${refWidth}px`;
		document.body.appendChild(ref);
	}

	const distance = Math.sqrt(dx * dx + dy * dy);

	let arcHeight = 100;
	if (typeof options.arcHeight === "number") {
		arcHeight = options.arcHeight;
	} else if (options.arcHeight) {
		arcHeight = options.arcHeight(distance);
	}

	let duration = 500;
	if (typeof options.duration === "number") {
		duration = options.duration;
	} else if (options.duration) {
		duration = options.duration(distance)
	}

	const controlPointX = (dx / 2) - arcHeight * (dy / distance);
	const controlPointY = (dy / 2) + arcHeight * (dx / distance);

	return new Promise<void>(resolve => {
		const animation = new Animation(({ progress: t }) => {
			if (t === 1) {
				if (options.clone && ref.parentNode) {
					ref.parentNode.removeChild(ref);
				}
				resolve();
				return;
			}

			// greatly simplified bezier curve animation, knowing we go from zero to dx/dy
			const u = 1 - t;
			const tt = t * t;
			const x = (2 * u * t * controlPointX) + (tt * dx);
			const y = (2 * u * t * controlPointY) + (tt * dy);

			let transform = `translate(${x}px, ${y}px)`;
			if (options.extraTransforms) {
				transform += " " + options.extraTransforms(t);
			}

			ref.style.transform = transform;
			if (options.opacity) {
				ref.style.opacity = String(options.opacity(t));
			}
		}, duration);

		animation.start();
	});
}